import React, {
  CSSProperties,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRightOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MessageOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Empty,
  Input,
  message,
  Popover,
  Result,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnProps } from 'antd/es/table';

import DataChangeRequestStatus, {
  DataChangeRequestValue,
} from '../components/dataChangeRequests/DataChangeRequestStatus';
import EditableDataChangeRequestLogo from '../components/dataChangeRequests/EditableDataChangeRequestLogo';
import { PaperPlaneOutlined } from '../components/MabelIcons';
import LiveValidatedAddress from '../components/utils/LiveAddressCheck';
import LanguageSelect from '../components/utils/LanguageSelect';
import { UserContext } from '../contexts/UserContext';
import {
  GetClientPracticesQuery,
  useCreateClientDataChangeRequestMutation,
  useDeleteClientDataChangeRequestMutation,
  useGetClientPracticesQuery,
  useUpdateClientDataChangeRequestMutation,
  useUpdateClientPracticeMutation,
} from '../graphql/generated';
import {
  draftToPracticeProposedChanges,
  getLivePracticeValues,
  getPracticeBaseline,
  originalValuesForPracticeChanges,
  practiceDraftsEqual,
  PracticeProposedChanges,
  practiceProposedChangesEqual,
  PracticeRowDraft,
  requestToPracticeProposedChanges,
  splitPracticeChangesForApproval,
} from './clientPracticeChanges';

type OrganizationNetwork = NonNullable<
  NonNullable<
    NonNullable<GetClientPracticesQuery['users_by_pk']>['client_organization']
  >['organization_networks']
>[0];
type ClientNetwork = OrganizationNetwork['network'];
type ClientPractice = ClientNetwork['practices'][0];
type ChangeRequest = ClientPractice['change_requests'][0];
type PracticeRow = ClientPractice & { network: ClientNetwork };
type ApprovedCampaignComponent = {
  campaignName: string;
  channel: 'Email' | 'Letter' | 'SMS';
  campaignIsArchived: boolean;
  componentIsArchived: boolean;
  isCancelled: boolean;
  sendDatetime: string;
};

const partitionCampaignComponentsByTiming = (
  components: ApprovedCampaignComponent[]
) => {
  const groups: {
    past: ApprovedCampaignComponent[];
    upcoming: ApprovedCampaignComponent[];
  } = { past: [], upcoming: [] };
  const now = dayjs();

  components.forEach((component) => {
    if (
      component.isCancelled ||
      component.componentIsArchived ||
      component.campaignIsArchived
    )
      return;

    const sendDatetime = dayjs(component.sendDatetime);
    if (!sendDatetime.isValid()) return;

    groups[sendDatetime.isAfter(now) ? 'upcoming' : 'past'].push(component);
  });

  groups.upcoming.sort(
    (a, b) => dayjs(a.sendDatetime).valueOf() - dayjs(b.sendDatetime).valueOf()
  );
  groups.past.sort(
    (a, b) => dayjs(b.sendDatetime).valueOf() - dayjs(a.sendDatetime).valueOf()
  );

  return groups;
};

const pageStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 1440,
};

const contentCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const editableCellStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
};

const inlineEditInputStyle: CSSProperties = {
  background: 'transparent',
  marginLeft: -6,
  minWidth: 0,
  paddingLeft: 6,
  paddingRight: 6,
};

const addressEditInputStyle: CSSProperties = {
  ...inlineEditInputStyle,
  marginLeft: 0,
};

const pendingControlStyle: CSSProperties = {
  borderRadius: 4,
  outline: '2px solid #d4b106',
  outlineOffset: 1,
};

const withPendingBorder = (
  style: CSSProperties,
  isPending: boolean
): CSSProperties => (isPending ? { ...style, ...pendingControlStyle } : style);

const pendingDeactivationRowStyle: CSSProperties = {
  background: '#e8e8e8',
};

const revertButtonStyle: CSSProperties = {
  marginLeft: 4,
};

const getPendingRequest = (practice: ClientPractice) =>
  practice.change_requests[0];

const isUnderReview = (practice: ClientPractice) =>
  getPendingRequest(practice)?.status === 'reviewing';

const proposes = (
  request: ChangeRequest | undefined,
  field: keyof PracticeProposedChanges
) => field in requestToPracticeProposedChanges(request);

const networkLabel = (network: Pick<ClientNetwork, 'network_name' | 'dc_id'>) =>
  network.dc_id
    ? `${network.network_name} (${network.dc_id})`
    : network.network_name;

const normalizeLanguageCode = (language?: string | null) =>
  language?.replace('/', '-') || undefined;

const getCampaignComponents = (practice: ClientPractice) =>
  partitionCampaignComponentsByTiming([
    ...practice.email_component_approvals.map((approval) => ({
      campaignName:
        approval.campaign_email_component.campaign_template.campaign_name,
      channel: 'Email' as const,
      campaignIsArchived:
        approval.campaign_email_component.campaign_template.is_archived,
      componentIsArchived: approval.campaign_email_component.is_archived,
      isCancelled: approval.is_cancelled,
      sendDatetime: approval.campaign_email_component.send_datetime,
    })),
    ...practice.letter_component_approvals.map((approval) => ({
      campaignName:
        approval.campaign_letter_component.campaign_template.campaign_name,
      channel: 'Letter' as const,
      campaignIsArchived:
        approval.campaign_letter_component.campaign_template.is_archived,
      componentIsArchived: approval.campaign_letter_component.is_archived,
      isCancelled: approval.is_cancelled,
      sendDatetime: approval.campaign_letter_component.send_datetime,
    })),
    ...practice.sms_component_approvals.map((approval) => ({
      campaignName:
        approval.campaign_sms_component.campaign_template.campaign_name,
      channel: 'SMS' as const,
      campaignIsArchived:
        approval.campaign_sms_component.campaign_template.is_archived,
      componentIsArchived: approval.campaign_sms_component.is_archived,
      isCancelled: approval.is_cancelled,
      sendDatetime: approval.campaign_sms_component.send_datetime,
    })),
  ]);

const CampaignChannelIcon: React.FC<{
  channel: 'Email' | 'Letter' | 'SMS';
}> = ({ channel }) => {
  if (channel === 'Letter') return <PaperPlaneOutlined />;

  return (
    <span aria-label={channel} role="img">
      {channel === 'Email' ? <MailOutlined /> : <MessageOutlined />}
    </span>
  );
};

const CampaignComponentList: React.FC<{
  components: ApprovedCampaignComponent[];
  emptyText: string;
  title: string;
  warning?: boolean;
}> = ({ components, emptyText, title, warning = false }) => (
  <Space direction="vertical" size={4} style={{ display: 'flex' }}>
    <Typography.Text strong type={warning ? 'warning' : undefined}>
      {title} ({components.length})
    </Typography.Text>
    {components.length > 0 ? (
      components.map((component, index) => (
        <Space
          align="start"
          key={`${component.channel}-${component.sendDatetime}-${index}`}
          size={6}
        >
          <CampaignChannelIcon channel={component.channel} />
          <div>
            <Typography.Text style={{ display: 'block' }}>
              {component.campaignName}
            </Typography.Text>
            <Typography.Text type="secondary">
              {dayjs(component.sendDatetime).format('lll')}
            </Typography.Text>
          </div>
        </Space>
      ))
    ) : (
      <Typography.Text type="secondary">{emptyText}</Typography.Text>
    )}
  </Space>
);

const RevertProposedFieldButton: React.FC<{
  current: string;
  onRevert: () => void;
}> = ({ current, onRevert }) => (
  <Tooltip title={`Revert to current value: ${current}`}>
    <Button
      aria-label="Revert pending field"
      icon={<UndoOutlined />}
      onClick={onRevert}
      size="small"
      style={revertButtonStyle}
      type="text"
    />
  </Tooltip>
);

const ClientPracticesPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [showArchivedPractices, setShowArchivedPractices] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, PracticeRowDraft>>({});
  const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const { data, loading, error } = useGetClientPracticesQuery({
    variables: { user_id: user?.user_id || '' },
    skip: !user?.user_id,
    notifyOnNetworkStatusChange: true,
  });
  const [createRequest] = useCreateClientDataChangeRequestMutation();
  const [updateRequest] = useUpdateClientDataChangeRequestMutation();
  const [deleteRequest] = useDeleteClientDataChangeRequestMutation();
  const [updatePractice] = useUpdateClientPracticeMutation();

  const organization = data?.users_by_pk?.client_organization;
  const organizationId = data?.users_by_pk?.client_organization_id;

  const networks = useMemo(() => {
    return (
      organization?.organization_networks
        .map(({ network }) => network)
        .filter((network) => !network.is_archived)
        .sort((a, b) => networkLabel(a).localeCompare(networkLabel(b))) || []
    );
  }, [organization]);

  const practices = useMemo(() => {
    const byId = new Map<string, PracticeRow>();
    networks.forEach((network) => {
      network.practices.forEach((practice) => {
        byId.set(practice.practice_id, { ...practice, network });
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.practice_name.localeCompare(b.practice_name)
    );
  }, [networks]);

  const visiblePractices = useMemo(
    () =>
      showArchivedPractices
        ? practices
        : practices.filter((practice) => !practice.is_archived),
    [practices, showArchivedPractices]
  );

  const networkOptions = useMemo(
    () =>
      networks.map((network) => ({
        label: networkLabel(network),
        value: network.network_id,
      })),
    [networks]
  );
  const networkNames = useMemo(
    () =>
      new Map(
        networks.map((network) => [network.network_id, network.network_name])
      ),
    [networks]
  );

  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const practicesRef = useRef(practices);
  practicesRef.current = practices;
  const inFlightRef = useRef<Record<string, boolean>>({});

  const getDraft = (practice: PracticeRow) =>
    drafts[practice.practice_id] || getPracticeBaseline(practice);

  const clearDraft = (practiceId: string) => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[practiceId];
      return next;
    });
  };

  const setDraftFields = (
    practice: PracticeRow,
    changes: Partial<PracticeRowDraft>
  ) => {
    const next = { ...getDraft(practice), ...changes };
    setDrafts((current) => ({
      ...current,
      [practice.practice_id]: next,
    }));
    return next;
  };

  const commitRow = (
    practice: PracticeRow,
    draftOverride?: PracticeRowDraft
  ) => {
    const practiceId = practice.practice_id;
    const draft = draftOverride || draftsRef.current[practiceId];
    if (
      !draft ||
      inFlightRef.current[practiceId] ||
      !organizationId ||
      isUnderReview(practice)
    )
      return;

    if (!draft.practice_name.trim()) {
      message.error("Practice name can't be empty.");
      setDraftFields(practice, {
        practice_name: getPracticeBaseline(practice).practice_name,
      });
      return;
    }

    const proposed = draftToPracticeProposedChanges(practice, draft);
    const pending = getPendingRequest(practice);
    const pendingChanges = requestToPracticeProposedChanges(pending);
    const hasUpcomingCampaigns =
      getCampaignComponents(practice).upcoming.length > 0;

    // SC-1105: Primary-network changes always require approval. Other practice
    // fields update directly unless an approved campaign is still upcoming.
    const { directChanges, requestedChanges } = splitPracticeChangesForApproval(
      proposed,
      hasUpcomingCampaigns
    );
    const hasDirectChanges = Object.keys(directChanges).length > 0;
    const hasRequestedChanges = Object.keys(requestedChanges).length > 0;
    const requestedChangesAreUnchanged = practiceProposedChangesEqual(
      requestedChanges,
      pendingChanges
    );

    if (!hasDirectChanges && requestedChangesAreUnchanged) {
      if (practiceDraftsEqual(draft, getPracticeBaseline(practice))) {
        clearDraft(practiceId);
      }
      return;
    }

    const mutations: Promise<unknown>[] = [];

    if (hasDirectChanges) {
      mutations.push(
        updatePractice({
          variables: {
            changes: directChanges,
            practice_id: practiceId,
          },
          refetchQueries: ['getClientPractices'],
          awaitRefetchQueries: true,
        })
      );
    }

    if (pending && !hasRequestedChanges) {
      mutations.push(
        deleteRequest({
          variables: {
            data_change_request_id: pending.data_change_request_id,
          },
          refetchQueries: ['getClientPractices'],
          awaitRefetchQueries: true,
        })
      );
    } else if (pending && !requestedChangesAreUnchanged) {
      mutations.push(
        updateRequest({
          variables: {
            data_change_request_id: pending.data_change_request_id,
            original_values: originalValuesForPracticeChanges(
              practice,
              requestedChanges
            ),
            proposed_changes: requestedChanges,
          },
          refetchQueries: ['getClientPractices'],
          awaitRefetchQueries: true,
        })
      );
    } else if (!pending && hasRequestedChanges) {
      mutations.push(
        createRequest({
          variables: {
            request: {
              change_type: 'update',
              client_organization_id: organizationId,
              original_values: originalValuesForPracticeChanges(
                practice,
                requestedChanges
              ),
              proposed_changes: requestedChanges,
              target_key: { practice_id: practiceId },
              target_table: 'practices',
            },
          },
          refetchQueries: ['getClientPractices'],
          awaitRefetchQueries: true,
        })
      );
    }

    if (mutations.length === 0) return;

    inFlightRef.current[practiceId] = true;
    setSavingIds((current) => ({ ...current, [practiceId]: true }));
    setSaveErrors((current) => {
      const next = { ...current };
      delete next[practiceId];
      return next;
    });

    Promise.all(mutations)
      .then(() => {
        inFlightRef.current[practiceId] = false;
        setSavingIds((current) => ({ ...current, [practiceId]: false }));

        const freshPractice = practicesRef.current.find(
          (candidate) => candidate.practice_id === practiceId
        );
        const latestDraft = draftsRef.current[practiceId];
        if (!freshPractice || !latestDraft) return;
        if (
          practiceDraftsEqual(latestDraft, getPracticeBaseline(freshPractice))
        ) {
          clearDraft(practiceId);
        } else {
          commitRow(freshPractice, latestDraft);
        }
      })
      .catch((saveError: { message?: string }) => {
        inFlightRef.current[practiceId] = false;
        setSavingIds((current) => ({ ...current, [practiceId]: false }));
        setSaveErrors((current) => ({
          ...current,
          [practiceId]: saveError.message || 'Save failed',
        }));
      });
  };

  const revertField = (
    practice: PracticeRow,
    field: keyof PracticeRowDraft
  ) => {
    const live = getLivePracticeValues(practice);
    const next = setDraftFields(practice, { [field]: live[field] });
    commitRow(practice, next);
  };

  const withdrawRequest = (practice: PracticeRow) => {
    const next = setDraftFields(practice, getLivePracticeValues(practice));
    commitRow(practice, next);
  };

  const getNetworkName = (networkId?: string | null) =>
    (networkId && networkNames.get(networkId)) || 'None';

  const pendingChangeLines = (practice: PracticeRow) => {
    const proposed = requestToPracticeProposedChanges(
      getPendingRequest(practice)
    );
    const lines: Array<{
      label: string;
      from: string | null;
      to: string | null;
    }> = [];

    if ('practice_name' in proposed)
      lines.push({
        label: 'Name',
        from: practice.practice_name,
        to: proposed.practice_name || null,
      });
    if ('practice_address' in proposed)
      lines.push({
        label: 'Address',
        from: practice.practice_address || null,
        to: proposed.practice_address || null,
      });
    if ('practice_language' in proposed)
      lines.push({
        label: 'Language',
        from: practice.practice_language || 'EN (default)',
        to: proposed.practice_language || 'EN (default)',
      });
    if ('practice_logo' in proposed)
      lines.push({
        label: 'Logo',
        from: practice.practice_logo ? 'Current logo' : null,
        to: proposed.practice_logo ? 'New logo' : null,
      });
    if ('primary_network_id' in proposed)
      lines.push({
        label: 'Network',
        from: getNetworkName(practice.primary_network_id),
        to: getNetworkName(proposed.primary_network_id),
      });
    if ('is_archived' in proposed)
      lines.push({
        label: 'Active',
        from: practice.is_archived ? 'No' : 'Yes',
        to: proposed.is_archived ? 'No' : 'Yes',
      });

    return lines;
  };

  const isProposedDeactivation = (practice: PracticeRow) =>
    !practice.is_archived && getDraft(practice).is_archived;

  const columns: ColumnProps<PracticeRow>[] = [
    {
      title: 'TIN / CCN',
      key: 'identifiers',
      width: 95,
      render: (_, practice) => (
        <Space direction="vertical" size={0}>
          {practice.tin ? (
            <Typography.Text>{practice.tin}</Typography.Text>
          ) : null}
          {practice.ccn ? (
            <Typography.Text type="secondary">
              CCN {practice.ccn}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Practice / Legal Name',
      key: 'practice_name',
      width: 185,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_name');
        return (
          <div>
            <div style={editableCellStyle}>
              <Input.TextArea
                className="editable-field"
                autoSize={{ minRows: 1 }}
                bordered={false}
                disabled={underReview}
                maxLength={40}
                onBlur={() => commitRow(practice)}
                onChange={(event) =>
                  setDraftFields(practice, {
                    practice_name: event.target.value.replace(/\n/g, ''),
                  })
                }
                onPressEnter={(event) => {
                  event.preventDefault();
                  (event.target as HTMLTextAreaElement).blur();
                }}
                style={withPendingBorder(
                  inlineEditInputStyle,
                  hasPendingChange
                )}
                value={getDraft(practice).practice_name}
              />
              {hasPendingChange && !underReview ? (
                <RevertProposedFieldButton
                  current={practice.practice_name}
                  onRevert={() => revertField(practice, 'practice_name')}
                />
              ) : null}
            </div>
            {practice.legal_business_name ? (
              <Tooltip title="Legal business name; contact support@getmabel.com to change">
                <Typography.Text
                  style={{ display: 'block', fontSize: 12 }}
                  type="secondary"
                >
                  {practice.legal_business_name}
                </Typography.Text>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Logo',
      key: 'practice_logo',
      width: 115,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_logo');
        const logoUrl = getDraft(practice).practice_logo;
        return (
          <EditableDataChangeRequestLogo
            currentLogoUrl={practice.practice_logo}
            disabled={underReview}
            label={practice.practice_name}
            logoUrl={logoUrl}
            onChange={(nextLogoUrl) => {
              const next = setDraftFields(practice, {
                practice_logo: nextLogoUrl,
              });
              commitRow(practice, next);
            }}
            onRevert={() => revertField(practice, 'practice_logo')}
            pending={hasPendingChange}
            uploadPath={`practice/${practice.practice_id}`}
          />
        );
      },
    },
    {
      title: 'Address',
      key: 'practice_address',
      width: 180,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_address');
        return (
          <div style={editableCellStyle}>
            <LiveValidatedAddress
              address={getDraft(practice).practice_address}
              storedAddress={practice.practice_address}
              storedDeliverability={practice.deliverability}
            />
            <Input.TextArea
              className="editable-field"
              autoSize={{ minRows: 1 }}
              bordered={false}
              disabled={underReview}
              onBlur={() => commitRow(practice)}
              onChange={(event) =>
                setDraftFields(practice, {
                  practice_address: event.target.value,
                })
              }
              style={withPendingBorder(addressEditInputStyle, hasPendingChange)}
              value={getDraft(practice).practice_address || ''}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={practice.practice_address || 'empty'}
                onRevert={() => revertField(practice, 'practice_address')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Language',
      key: 'practice_language',
      width: 110,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'practice_language');
        return (
          <div style={editableCellStyle}>
            <LanguageSelect
              disabled={underReview}
              dropdownMatchSelectWidth={260}
              onChange={(value) => {
                const next = setDraftFields(practice, {
                  practice_language: value,
                });
                commitRow(practice, next);
              }}
              optionLabelProp="value"
              placeholder="EN"
              style={withPendingBorder(
                { flex: 1, minWidth: 0 },
                hasPendingChange
              )}
              value={normalizeLanguageCode(
                getDraft(practice).practice_language
              )}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={practice.practice_language || 'EN (default)'}
                onRevert={() => revertField(practice, 'practice_language')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Network',
      key: 'network',
      width: 165,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'primary_network_id');
        return (
          <div style={editableCellStyle}>
            <Select
              disabled={underReview}
              onChange={(value) => {
                const next = setDraftFields(practice, {
                  primary_network_id: value,
                });
                commitRow(practice, next);
              }}
              options={networkOptions}
              style={withPendingBorder(
                { flex: 1, minWidth: 0 },
                hasPendingChange
              )}
              value={getDraft(practice).primary_network_id || undefined}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={getNetworkName(practice.primary_network_id)}
                onRevert={() => revertField(practice, 'primary_network_id')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Active',
      key: 'active',
      width: 65,
      render: (_, practice) => {
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        const hasPendingChange = proposes(pending, 'is_archived');
        return (
          <div style={editableCellStyle}>
            <Switch
              checked={!getDraft(practice).is_archived}
              disabled={underReview}
              onChange={(checked) => {
                const next = setDraftFields(practice, {
                  is_archived: !checked,
                });
                commitRow(practice, next);
              }}
              style={withPendingBorder({}, hasPendingChange)}
            />
            {hasPendingChange && !underReview ? (
              <RevertProposedFieldButton
                current={practice.is_archived ? 'Inactive' : 'Active'}
                onRevert={() => revertField(practice, 'is_archived')}
              />
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Campaigns',
      key: 'upcoming_campaigns',
      align: 'center',
      width: 95,
      render: (_, practice) => {
        const components = getCampaignComponents(practice);
        const hasUpcomingCampaigns = components.upcoming.length > 0;
        const popoverContent = (
          <div style={{ maxHeight: 360, overflowY: 'auto', width: 360 }}>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: 'flex' }}
            >
              <CampaignComponentList
                components={components.upcoming}
                emptyText="No upcoming campaigns"
                title="Upcoming"
                warning={hasUpcomingCampaigns}
              />
              <CampaignComponentList
                components={components.past}
                emptyText="No past campaigns"
                title="Past"
              />
              {hasUpcomingCampaigns ? (
                <Typography.Text type="warning">
                  Practice changes may affect upcoming campaigns.
                </Typography.Text>
              ) : null}
            </Space>
          </div>
        );

        return (
          <Popover
            content={popoverContent}
            placement="left"
            title="Approved campaigns"
            trigger="hover"
          >
            <span
              aria-label={`${components.upcoming.length} upcoming ${
                components.upcoming.length === 1 ? 'campaign' : 'campaigns'
              } and ${components.past.length} past ${
                components.past.length === 1 ? 'campaign' : 'campaigns'
              }`}
              role="img"
              style={{
                color: hasUpcomingCampaigns ? '#d48806' : '#bfbfbf',
                cursor: 'help',
                fontSize: 18,
              }}
            >
              {hasUpcomingCampaigns ? (
                <WarningOutlined />
              ) : (
                <InfoCircleOutlined />
              )}
            </span>
          </Popover>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 105,
      render: (_, practice) => {
        const practiceId = practice.practice_id;
        const pending = getPendingRequest(practice);
        const underReview = isUnderReview(practice);
        return (
          <DataChangeRequestStatus
            error={saveErrors[practiceId]}
            pending={Boolean(pending)}
            pendingContent={
              pending ? (
                <Space direction="vertical">
                  <div>
                    {pendingChangeLines(practice).map(({ label, from, to }) => (
                      <div key={label}>
                        <Typography.Text type="secondary">
                          {label}:{' '}
                        </Typography.Text>
                        <DataChangeRequestValue previous value={from} />{' '}
                        <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
                        <DataChangeRequestValue value={to} />
                      </div>
                    ))}
                  </div>
                  {pending.submission_note ? (
                    <Typography.Text type="secondary">
                      Note: {pending.submission_note}
                    </Typography.Text>
                  ) : null}
                  {!underReview ? (
                    <Button
                      danger
                      onClick={() => withdrawRequest(practice)}
                      size="small"
                    >
                      Withdraw request
                    </Button>
                  ) : null}
                </Space>
              ) : undefined
            }
            retry={() => commitRow(practice)}
            reviewing={underReview}
            saving={Boolean(savingIds[practiceId])}
          />
        );
      },
    },
  ];

  const columnsWithRowState = columns.map((column) => ({
    ...column,
    onCell: (practice: PracticeRow) => ({
      style: isProposedDeactivation(practice)
        ? pendingDeactivationRowStyle
        : undefined,
    }),
  }));

  if (loading && !data) return <Skeleton active />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving practices"
        subTitle={error.message}
      />
    );
  if (!organizationId || !organization)
    return (
      <Result
        status="warning"
        title="No client organization found"
        subTitle="Your account must be attached to a client organization before you can request practice changes."
      />
    );

  return (
    <>
      <Helmet>
        <title>Practices | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          Practices
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ fontSize: 16 }}>
          Edit practice details inline. Changes to practices with upcoming
          campaigns will be marked as pending until Mabel can review.
        </Typography.Paragraph>

        <div style={{ marginBottom: 8, textAlign: 'right' }}>
          <Space>
            <Typography.Text>Show archived practices</Typography.Text>
            <Switch
              aria-label="Show archived practices"
              checked={showArchivedPractices}
              onChange={setShowArchivedPractices}
            />
          </Space>
        </div>

        <Card bodyStyle={{ padding: 0 }} style={contentCardStyle}>
          <Table<PracticeRow>
            columns={columnsWithRowState}
            dataSource={visiblePractices}
            locale={{
              emptyText: (
                <Empty
                  description={
                    showArchivedPractices
                      ? 'No practices are attached to this organization.'
                      : 'No active practices are attached to this organization.'
                  }
                />
              ),
            }}
            pagination={{ hideOnSinglePage: true, pageSize: 25 }}
            rowKey="practice_id"
            scroll={{ x: 1115 }}
            size="small"
            tableLayout="fixed"
          />
        </Card>
      </div>
    </>
  );
};

export default ClientPracticesPage;
